// Messenger / WhatsApp inbox — the floating-bubble data + the admin
// config surface. Inbound (Messenger + WhatsApp-via-Page-Inbox echoes)
// arrives through the public webhook; outbound replies are Messenger-
// only (WhatsApp customers are answered from the FB Page Inbox app,
// which round-trips back as echo events). Go side: internal/messengerstatus.

import type { ApiClient } from "./client.js";
import type {
  MessengerBubbleResponse,
  MessengerClaimResult,
  MessengerConversationResponse,
  MessengerOpResult,
  MessengerSaveRequest,
  MessengerSaveResult,
  MessengerSendResult,
  MessengerStateResponse,
  MessengerTemplate,
  MessengerTemplateListResponse,
  MessengerTemplateSaveResponse,
  MessengerTestResult,
} from "./types.js";

export type { MessengerTemplate };

export function makeMessengerApi(client: ApiClient) {
  function creds(req: MessengerSaveRequest): Record<string, unknown> {
    const b: Record<string, unknown> = { app_id: req.app_id, page_id: req.page_id };
    if (req.app_secret) b.app_secret = req.app_secret;
    if (req.page_token) b.page_token = req.page_token;
    if (req.verify_token) b.verify_token = req.verify_token;
    if (req.extra) b.extra = req.extra;
    return b;
  }
  return {
    /** The floating-bubble payload — unread total + recent conversations. */
    bubble(limit?: number) {
      return client.get<MessengerBubbleResponse>("/v1/api/messenger/bubble", limit ? { limit } : undefined);
    },
    /** One conversation thread (header + message list + whether reply is supported). */
    conversation(id: number) {
      return client.get<MessengerConversationResponse>("/v1/api/messenger/conversation", { id });
    },
    /** The admin config-card data (provider creds [no secrets] + a live connection probe). */
    state() {
      return client.get<MessengerStateResponse>("/v1/api/messenger/state");
    },
    /** Save the Meta provider credentials — admin only. Empty/omitted secret
     *  fields keep the stored ciphertext. */
    save(req: MessengerSaveRequest) {
      return client.post<MessengerSaveResult>("/v1/api/messenger/save", creds(req));
    },
    /** Probe connectivity without persisting (empty fields fall back to stored). */
    test(req: MessengerSaveRequest) {
      return client.post<MessengerTestResult>("/v1/api/messenger/test", creds(req));
    },
    /** Send a reply (Messenger only — the worker dispatches to Meta async).
     *  A `whatsapp_outbound_unsupported` error means "reply via Page Inbox". */
    send(conversationId: number, body: string) {
      return client.post<MessengerSendResult>("/v1/api/messenger/send", { conversation_id: conversationId, body });
    },
    /** Mark a conversation read (also sends Meta's "seen ✓" receipt for Messenger). */
    markRead(conversationId: number) {
      return client.post<{ ok: boolean; error?: string }>("/v1/api/messenger/mark_read", { conversation_id: conversationId });
    },
    /** Claim a conversation. `claimed:false` ⇒ someone else holds it (use `takeOver`). */
    claim(conversationId: number) {
      return client.post<MessengerClaimResult>("/v1/api/messenger/claim", { conversation_id: conversationId });
    },
    /** Take a conversation away from whoever holds it ("yes, I want this anyway"). */
    takeOver(conversationId: number) {
      return client.post<MessengerOpResult>("/v1/api/messenger/take_over", { conversation_id: conversationId });
    },
    /** Drop the assignment on a conversation. */
    release(conversationId: number) {
      return client.post<MessengerOpResult>("/v1/api/messenger/release", { conversation_id: conversationId });
    },
    /** Manually link a Messenger conversation to a radius_subscribers row
     *  when the auto-link (phone-tail match) didn't fire — used by the
     *  bubble's "Link to subscriber" button so anonymous public PSIDs
     *  get an internal identity. Empty username unlinks. */
    linkSubscriber(conversationId: number, username: string) {
      return client.post<{ ok: boolean; error?: string }>("/v1/api/messenger/link_subscriber", {
        conversation_id: conversationId,
        username,
      });
    },
    /** Wipe the rolling telemetry for one action key (e.g. "send") — admin only. */
    clearHistory(action: string) {
      return client.post<{ ok: boolean; error?: string }>("/v1/api/messenger/clear_history", { action });
    },
    /** Canned-response templates. listTemplates is open to admin+support
     *  (the bubble compose dropdown needs it); save/delete are admin-only. */
    listTemplates() {
      return client.get<MessengerTemplateListResponse>("/v1/api/messenger/templates");
    },
    saveTemplate(req: { id?: number; label: string; body: string }) {
      return client.post<MessengerTemplateSaveResponse>("/v1/api/messenger/templates/save", {
        id: req.id ?? 0,
        label: req.label,
        body: req.body,
      });
    },
    deleteTemplate(id: number) {
      return client.post<{ ok: boolean; error?: string }>("/v1/api/messenger/templates/delete", { id });
    },
  };
}

export type MessengerApi = ReturnType<typeof makeMessengerApi>;
